'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { X, Cookie } from 'lucide-react'
import { cookieConsent, initializeAnalytics } from '@/lib/analytics'
import { getCurrentLocale, translateText } from '@/lib/utils'

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [currentLocale, setCurrentLocale] = useState<'en' | 'es' | 'fr'>('fr')

  useEffect(() => {
    setIsLoaded(true)
    setCurrentLocale(getCurrentLocale())
    // Show banner if consent not given and not explicitly denied
    const hasConsent = cookieConsent.hasConsent()
    const hasDenied = localStorage.getItem('analytics-consent') === 'false'

    if (!hasConsent && !hasDenied) {
      setShowBanner(true)
    }
  }, [])

  const handleAccept = () => {
    cookieConsent.setConsent(true)
    setShowBanner(false)
    initializeAnalytics()
  }

  const handleDecline = () => {
    cookieConsent.setConsent(false)
    setShowBanner(false)
  }

  const handleDismiss = () => {
    setShowBanner(false)
  }

  if (!isLoaded || !showBanner) {
    return null
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:max-w-md">
      <Card className="shadow-lg border-2">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Cookie className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-sm mb-2">{translateText('{{cookieConsent.title}}', currentLocale)}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {translateText('{{cookieConsent.message}}', currentLocale)}
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" onClick={handleAccept}>
                  {translateText('{{cookieConsent.accept}}', currentLocale)}
                </Button>
                <Button variant="outline" size="sm" onClick={handleDecline}>
                  {translateText('{{cookieConsent.decline}}', currentLocale)}
                </Button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{translateText('{{cookieConsent.dismiss}}', currentLocale)}</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
